import { Chip, SvgIcon } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import LinearProgress from "@material-ui/core/LinearProgress";
import { ImportExport, InsertDriveFile, Mail } from "@material-ui/icons";
import CloudUpload from "@material-ui/icons/CloudUpload";
import Publish from "@material-ui/icons/Publish";
import Restore from "@material-ui/icons/Restore";
import createStyles from "@material-ui/styles/createStyles";
import makeStyles from "@material-ui/styles/makeStyles";
import { useSnackbar } from "notistack";
import React, { FC, useEffect, useState } from "react";
import { FileWithPreview } from "react-dropzone";
import { useIntl } from "react-intl";
import { useHistory, useParams } from "react-router";
import { ActionMeta, ValueType } from "react-select/src/types";
import { routesPaths } from "../../Routes";
import { ApiResponse } from "../../api/CustomFetch";
import {
  useAddExamSessionLocationsMutation,
  useCreateExamSessionMutation,
  useDeleteExamSessionLocationMutation,
  useDeleteExamSessionMutation,
  useExportOrdersMutation,
  useGetExamSessionQuery,
  usePublishExamResultsMutation,
  useSendInvitationMutation,
  useSendResultNotificationsMutation,
  useStartSurveyMutation,
  useUnPublishExamSessionMutation,
  useUpdateAndPublishExamSessionMutation,
  useUpdateExamSessionMutation,
} from "../../api/rtk/ExamSessionApi";
import { useCalculateExamResultsMutation } from "../../api/rtk/ExamSessionCandidateApi";
import { useGetLocationsQuery } from "../../api/rtk/LocationApi";
import { NewEntityId, SwissTimeZone } from "../../data/constants";
import { ExamScoreResultModel } from "../../model/ExamScoreResultModel";
import { ExamSessionViewModel, GetLocalizedName, initialExamSession } from "../../model/ExamSessionViewModel";
import { PersonType } from "../../model/PersonType";
import { GetTranslation } from "../../translations/GetTranslation";
import { TranslationKey } from "../../translations/TranslationKey";
import { navigateBack } from "../../utils/Navigation";
import CustomButton from "../common/buttons/CustomButton";
import CustomSplitButton from "../common/buttons/CustomSplitButton";
import DefaultButtons from "../common/buttons/DefaultButtons";
import { OptionType } from "../common/customSelect/CustomSelect";
import TranslatedMessage from "../translations/TranslatedMessage";
import ConfirmPublishResultsDialog from "./ConfirmPublishResultsDialog";
import ExamSessionExams from "./ExamSessionExams";
import ExamSessionFields from "./ExamSessionFields";
import ExamSessionFieldsReadonly from "./ExamSessionFieldsReadonly";
import ExamSessionLocationList from "./ExamSessionLocationList";
import ImportScoresAndGradesExamDialog from "./ImportExamScoresAndGradesDialog";
import InvitationDialog from "./InvitationDialog";
import { ReactComponent as CalculatorIcon } from "./assets/calculator-svgrepo-com.svg";
import ExamSessionCandidateList from "./examSessionCandidate/ExamSessionCandidateList";
import TitleBar from "../layout/TitleBar";
import moment from "moment";

const useStyles = makeStyles(() =>
  createStyles({
    locationsSelect: {
      marginTop: "4px",
    },
  })
);

const ExamSessionForm: FC = () => {
  const intl = useIntl();
  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();
  const { examSessionId, isLife } = useParams<{ examSessionId: string, isLife: string }>();

  const [examSession, setExamSession] = useState<ExamSessionViewModel>({...initialExamSession, isLife: isLife === "true" });
  const [isSurveyDialogOpen, setIsSurveyDialogOpen] = useState(false);
  const [isSurveyDeadlineEmpty, setIsSurveyDeadlineEmpty] = useState(false);
  const [locationIds, setLocationIds] = useState<string[]>([]);
  const [isInvitationDialogOpen, setIsInvitationDialogOpen] = useState(false);
  const [selectionValue, setSelectionValue] = React.useState("expert");
  const [personType, setPersonType] = React.useState(PersonType.Expert);
  const [isAllSelected, setIsAllSelected] = useState(true);
  const [selectedLocationsOptions, setSelectedLocationsOptions] = useState<OptionType[]>([]);
  const [selectedLocationIds, setSelectedLocationIds] = useState<string[]>([]);
  const [selectedIndex, setSelectedIndex] = React.useState(0);
  let mailingOptions = [
    GetTranslation(intl, "examSession.mail.invitation"),
    GetTranslation(intl, "examSession.mail.survey"),
  ];
  if (examSession.resultsArePublished) {
    mailingOptions = [...mailingOptions, GetTranslation(intl, "examSession.mail.notifyCandidates")];
  }
  const [mailButtonTooltip, setMailButtonTooltip] = React.useState(mailingOptions[selectedIndex]);
  const [isExamScoresAndGradesImportDialogOpen, setExamScoresAndGradesImportDialogOpen] = useState(false);
  const [isConfirmPublishResultsDialogOpen, setIsConfirmPublishResultsDialogOpen] = useState(false);
  const cssClasses = useStyles({});

  const [publishExamResults, { isLoading: isPublishExamResultsLoading }] = usePublishExamResultsMutation();
  const [exportOrders, { isLoading: isExportingOrders }] = useExportOrdersMutation();
  const [calculateExamResults, { isLoading: isCalculateExamResultsLoading }] = useCalculateExamResultsMutation();
  const [deleteExamSession, { isLoading: isDeleteExamSessionLoading, isSuccess: isDeleteSuccess }] =
    useDeleteExamSessionMutation();
  const [createExamSession, { isLoading: isCreateExamSessionLoading }] = useCreateExamSessionMutation();
  const [updateExamSession, { isLoading: isUpdateExamSessionLoading }] = useUpdateExamSessionMutation();
  const [updateAndPublishExamSession, { isLoading: isUpdateAndPublishExamSessionLoading }] =
    useUpdateAndPublishExamSessionMutation();
  const [unPublishExamSession, { isLoading: isUnPublishExamSessionLoading }] = useUnPublishExamSessionMutation();
  const [addExamSessionLocations, { isLoading: isAddExamSessionLocationsLoading }] =
    useAddExamSessionLocationsMutation();
  const [deleteExamSessionLocation, { isLoading: isDeleteExamSessionLocationLoading }] =
    useDeleteExamSessionLocationMutation();
  const [startSurvey, { isLoading: isStartSurveyLoading }] = useStartSurveyMutation();
  const [sendInvitation, { isLoading: isSendInvitationLoading }] = useSendInvitationMutation();
  const [sendResultNotifications, { isLoading: isSendResultNotificationsLoading }] =
    useSendResultNotificationsMutation();
  const { data: fetchedExamSession, isFetching: isExamSessionFetching } = useGetExamSessionQuery(examSessionId, {
    skip: examSessionId === NewEntityId || isDeleteSuccess,
  });
  const { data: allLocations, isFetching: isLocationsFetching } = useGetLocationsQuery();
  const isLoading =
    isExamSessionFetching ||
    isLocationsFetching ||
    isCalculateExamResultsLoading ||
    isPublishExamResultsLoading ||
    isDeleteExamSessionLoading ||
    isUpdateExamSessionLoading ||
    isCreateExamSessionLoading ||
    isUpdateExamSessionLoading ||
    isUpdateAndPublishExamSessionLoading ||
    isUnPublishExamSessionLoading ||
    isAddExamSessionLocationsLoading ||
    isDeleteExamSessionLocationLoading ||
    isStartSurveyLoading ||
    isSendInvitationLoading ||
    isSendResultNotificationsLoading ||
    isExportingOrders;

  useEffect(() => {
    if (fetchedExamSession !== undefined) {
      setExamSession(fetchedExamSession);
    }
  }, [fetchedExamSession]);

  useEffect(() => {
    if (examSession === undefined) {
      return;
    }

    setLocationIds(examSession.locations.map((loc) => loc.id));
  }, [examSession]);

  if (isLoading) {
    return <LinearProgress variant="query" />;
  }

  if ((fetchedExamSession === undefined && examSessionId !== NewEntityId) || allLocations === undefined) {
    return <></>;
  }

  const navigateToExamSessionsList = () => navigateBack(routesPaths.examSessions(), history, history.location);

  const handleChange = (partialExamSession: Partial<ExamSessionViewModel>) =>
    setExamSession((prevExamSession: any) => ({ ...prevExamSession, ...partialExamSession }));

  const handleClose = () => navigateToExamSessionsList();

  const handleSave = async () => {
    const examSessionToSave = {
      ...examSession,
      signupStartDate: moment(examSession.signupStartDate).tz(SwissTimeZone, true).utc().toDate(),
      signupEndDate: moment(examSession.signupEndDate).tz(SwissTimeZone, true).utc().toDate()
    };

    if (examSession.id === NewEntityId) {
      const response = await createExamSession(examSessionToSave).unwrap();
      if (response.success) {
        const examSessionId = response.data;
        history.replace(routesPaths.examSession(examSessionId));
      }
    } else {
      updateExamSession(examSessionToSave);
    }
  };

  const handleDelete = async () => {
    const response = await deleteExamSession(examSession.id).unwrap();
    if (response.success) {
      navigateToExamSessionsList();
    }
  };

  const handleAssignLocations = (locationIds: string[]) => {
    addExamSessionLocations({ examSessionId: examSession.id, locationIds });
  };

  const handleRemoveLocation = (locationId: string) =>
    deleteExamSessionLocation({ examSessionId: examSession.id, locationId });

  const handlePublishExamSession = () => updateAndPublishExamSession(examSession);

  const handleStartSurveyForExpert = () => {
    if (examSession.surveyDeadline === undefined) {
      setIsSurveyDeadlineEmpty(true);
    } else {
      startSurvey({ examSessionId: examSession.id, surveyDeadline: examSession.surveyDeadline });
      setIsSurveyDialogOpen(false);
    }
  };

  const handleUnPublishExamSession = () => unPublishExamSession(examSession.id);

  const handleLocationCardSelected = (locationId: string) =>
    history.push(routesPaths.examSessionLocation(examSession.id, locationId));

  const handleInvitation = () => setIsInvitationDialogOpen(true);

  const handleExamScoresAndGrades = () => setExamScoresAndGradesImportDialogOpen(true);

  const handlePersonTypeSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectionValue((event.target as HTMLInputElement).value);
    if (selectionValue === "candidate") {
      setPersonType(PersonType.Expert);
    } else {
      setPersonType(PersonType.Candidate);
    }
  };

  const handleSwitch = () => (event: React.ChangeEvent<HTMLInputElement>) => setIsAllSelected(event.target.checked);

  const handleLocationSelection = (selectedElements: ValueType<OptionType>, actionMeta?: ActionMeta<OptionType>) => {
    if ((actionMeta && actionMeta.action === "clear") || !selectedElements || isAllSelected) {
      setSelectedLocationsOptions([]);
    }

    const selectedElementsArray = selectedElements as OptionType[];

    if (selectedElementsArray && selectedElementsArray.length > 0) {
      setSelectedLocationsOptions(selectedElementsArray);
      const value: string[] = [];
      for (let i = 0, l = selectedElementsArray.length; i < l; i += 1) {
        const item = allLocations.find((loc) => loc.id === selectedElementsArray[i].value);
        if (item) {
          value.push(item.id);
        }
      }
      setSelectedLocationIds(value);
    }
  };

  const handleInvitationAccept = async () => {
    setIsInvitationDialogOpen(false);
    const invitationLocationIds = isAllSelected ? locationIds : selectedLocationIds;
    const response = await sendInvitation({
      examSessionId: examSession.id,
      locationIds: invitationLocationIds,
      personType,
    }).unwrap();
    if (personType === PersonType.Candidate) {
      enqueueSnackbar(
        <TranslatedMessage
          id="examSession.invitation.snackbar.candidates"
          values={{ count: response.candidatesNotified }}
        />
      );
    } else {
      enqueueSnackbar(
        <TranslatedMessage
          id="examSession.invitation.snackbar.experts"
          values={{ count: response.assignedExpertsNotified }}
        />
      );
    }
    resetInvitationStates();
  };

  const handleNotifyCandidates = () => sendResultNotifications(examSession.id);

  const handleMailButton = (index: number) => {
    switch (index) {
      case 0: {
        handleInvitation();
        break;
      }
      case 1: {
        setIsSurveyDialogOpen(true);
        break;
      }
      case 2: {
        handleNotifyCandidates();
        break;
      }
      default: {
        break;
      }
    }
  };

  const resetInvitationStates = () => {
    setIsInvitationDialogOpen(false);
    setSelectedLocationsOptions([]);
    setIsAllSelected(true);
    setSelectionValue("expert");
  };

  const handleUploadExamScoresAndGradesSuccess = (
    file: FileWithPreview,
    response: ApiResponse<ExamScoreResultModel>
  ) => {
    const { updatedExamsCount, recordsCount: totalCandidatesCount } = response.data;
    enqueueSnackbar(
      <>
        <TranslatedMessage id="examSession.writtenExamScoresImportDialog.uploadSuccess" />{" "}
        {`${updatedExamsCount}/${totalCandidatesCount}`}
      </>
    );
  };

  const handleUploadExamScoresAndGradesError = () => {
    enqueueSnackbar(<TranslatedMessage id="common.uploadError" />);
  };

  const handleMenuItemClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>, index: number) => {
    setMailButtonTooltip(mailingOptions[index]);
    setSelectedIndex(index);
    handleMailButton(index);
  };

  const handleConfirmationMailHistory = (locationId: string) => {
    let flagged = false;
    const confirmationMailHistory = examSession.confirmationMailHistory.find(
      (confirmation) => confirmation.locationId === locationId
    );
    if (confirmationMailHistory) {
      flagged = true;
    }
    return flagged;
  };

  const handlePublishExamResults = () => {
    setIsConfirmPublishResultsDialogOpen(false);
    publishExamResults(examSessionId);
  };

  const sessionHasRooms = examSession.examDays.filter((examDay) => examDay.roomAssignments.length > 0).length > 0;

  const publishButton = !examSession.published && (
    <CustomButton
      disabled={!sessionHasRooms}
      tooltip={<TranslatedMessage id="common.publish" />}
      onClick={handlePublishExamSession}
    >
      <Publish />
    </CustomButton>
  );

  const unpublishButton = examSession.published && (
    <CustomButton tooltip={<TranslatedMessage id="common.unpublish" />} onClick={handleUnPublishExamSession}>
      <Restore />
    </CustomButton>
  );

  const mailButton = examSession.published && (
    <CustomSplitButton
      tooltip={mailButtonTooltip}
      onClick={() => handleMailButton(selectedIndex)}
      options={mailingOptions}
      onMenuItemClick={handleMenuItemClick}
    >
      <Mail />
    </CustomSplitButton>
  );
  const exportButton = examSession.published && (
    <CustomButton
      disabled={isCalculateExamResultsLoading}
      tooltip={<TranslatedMessage id="examSession.button.exportOrders" />}
      onClick={() => exportOrders(examSessionId)}
    >
      <ImportExport />
    </CustomButton>
  );

  const calculateExamResultsButton = examSession.published && (
    <CustomButton
      disabled={isCalculateExamResultsLoading}
      tooltip={<TranslatedMessage id="examSession.button.calculateExamResults" />}
      onClick={() => calculateExamResults(examSessionId)}
    >
      <SvgIcon component={CalculatorIcon} viewBox="0 0 460 460" />
    </CustomButton>
  );

  const publishExamResultsButton = examSession.published && (
    <CustomButton
      disabled={examSession.resultsArePublished}
      tooltip={<TranslatedMessage id="examSession.button.publishExamResults" />}
      onClick={() => setIsConfirmPublishResultsDialogOpen(true)}
    >
      <InsertDriveFile />
    </CustomButton>
  );

  const importScoresAndGradesByExamsButton = examSession.published && (
    <CustomButton
      disabled={!sessionHasRooms}
      tooltip={<TranslatedMessage id="examSession.button.importExamScoresAndGrades" />}
      onClick={handleExamScoresAndGrades}
    >
      <CloudUpload />
    </CustomButton>
  );

  const locationsOptions: OptionType[] = Object.values(examSession.locations).map((loc) => ({
    value: loc.id,
    label: loc.name,
    flagged: handleConfirmationMailHistory(loc.id),
    flaggedTooltip: GetTranslation(intl, "examSession.invitation.flagged"),
  }));

  const getExamSessionStateTranslationKey = (published: boolean): TranslationKey =>
    published ? "common.status.published" : "common.status.draft";

  return (
    <>
      <Grid container={true} direction="column" spacing={3}>
        <TitleBar title={GetLocalizedName(examSession)} variant="h4">
          <DefaultButtons
            additionalButtons={
              <>
                <Chip
                  label={GetTranslation(intl, getExamSessionStateTranslationKey(examSession.published))}
                  style={{ marginRight: 20 }}
                />
                {examSessionId !== NewEntityId && publishButton}
                {unpublishButton}
                {importScoresAndGradesByExamsButton}
                {calculateExamResultsButton}
                {publishExamResultsButton}
                {mailButton}
                {exportButton}
              </>
            }
            onClose={handleClose}
            onDelete={handleDelete}
            onSave={handleSave}
            isDeleteButtonEnabled={examSession.locations.length === 0}
            isSaveButtonEnabled={!examSession.published}
          />
        </TitleBar>

        <Grid item={true}>
          {!examSession.published && (
            <ExamSessionFields examSession={examSession} onPropertyValueChanges={handleChange} />
          )}
          {examSession.published && (
            <ExamSessionFieldsReadonly
              examSession={examSession}
              isSurveyDialogOpen={isSurveyDialogOpen}
              isSurveyDeadlineEmpty={isSurveyDeadlineEmpty}
              onPropertyValueChanges={handleChange}
              onAcceptSurvey={handleStartSurveyForExpert}
              onCancelSurvey={() => setIsSurveyDialogOpen(false)}
            />
          )}
        </Grid>
        {examSession.published && (
          <Grid item={true}>
            <ExamSessionCandidateList isLife={examSession.isLife} />
          </Grid>
        )}

        <Grid item={true} container={true}>
          {examSession.id !== NewEntityId && (
            <ExamSessionLocationList
              isReadOnly={examSession.published}
              onRemovingLocation={handleRemoveLocation}
              className={cssClasses.locationsSelect}
              examSessionLocations={examSession.locations}
              allLocations={allLocations.filter((loc) => loc.rooms.length > 0)}
              onAssigningLocations={handleAssignLocations}
              onLocationCardSelected={handleLocationCardSelected}
            />
          )}
        </Grid>
        <Grid item={true}>
          {examSession.id !== NewEntityId && (
            <ExamSessionExams
              exams={examSession.exams}
              handleAddExams={(exams) => setExamSession({ ...examSession, exams: [...examSession.exams, ...exams] })}
              handleEditExam={(changedExam, courseTypes) =>
                setExamSession({
                  ...examSession,
                  exams: [...examSession.exams].map((exam) =>
                    exam.id === changedExam.id ? { ...exam, courseTypes: courseTypes } : exam
                  ),
                })
              }
              handleRemoveExam={(exam) =>
                setExamSession({ ...examSession, exams: examSession.exams.filter((e) => e.id !== exam.id) })
              }
              isReadOnly={examSession.published}
            />
          )}
        </Grid>
      </Grid>

      <ImportScoresAndGradesExamDialog
        isOpen={isExamScoresAndGradesImportDialogOpen}
        onClose={() => setExamScoresAndGradesImportDialogOpen(false)}
        onFileUploaded={handleUploadExamScoresAndGradesSuccess}
        onUploadError={handleUploadExamScoresAndGradesError}
        examSessionId={examSession.id}
      />
      <InvitationDialog
        onAccept={handleInvitationAccept}
        onCancel={resetInvitationStates}
        onRadioButtonChange={handlePersonTypeSelect}
        onSwitchChange={handleSwitch()}
        onLocationChange={handleLocationSelection}
        isAllSelected={isAllSelected}
        isInvitationDialogOpen={isInvitationDialogOpen}
        selectionValue={selectionValue}
        locations={locationsOptions}
        selectedLocations={selectedLocationsOptions}
      />
      <ConfirmPublishResultsDialog
        isOpen={isConfirmPublishResultsDialogOpen}
        onAccept={handlePublishExamResults}
        onCancel={() => setIsConfirmPublishResultsDialogOpen(false)}
      />
    </>
  );
};

export default ExamSessionForm;
